import { Moment } from 'moment/moment';
import round from 'lodash/round';

import {
  BasketContent,
  CategoryReference,
  ChildcareBasketPrices,
  ChildcareRecurrentBasketPrices,
  GiftCard,
  Prestation,
  PrestationReference,
  Promotion,
  Schedule,
  SubcategoryReference,
  Universe,
  YoungestChildAge,
  ZoneId,
} from '../types/commons';
import { RECURRING_CHILDCARE } from '../constants/prestations';
import { HOURLY_SURCHARGE } from '../constants/prices';
import { getAllBasketPrestations, getDurationPerWeekFromSchedules } from '../utils/basket';
import { isEligibleForAllowance } from '../utils/childcare';
import { CATEGORIES } from '../constants/universes';

import {
  calculatePromoAndGiftValues,
  getDailyAppointmentSurchargeReason,
  getPriceByZoneOrPriceItself,
  isAppointmentDailySurcharged,
} from './common-prices';

export const DEFAULT_CHILDCARE_BASKET_PRICES: ChildcareBasketPrices = {
  kind: 'childcare',
  weeklyTotalPrice: 0,
  allowanceAmount: 0,
  totalPriceAfterAllowance: 0,
  totalPriceTaxCredit: 0,
  monthlyEstimation: 0,
  proPrice: 0,
  hourlyPrice: 0,
  totalHourlyPrice: 0,
  totalHourlyPriceAfterTax: 0,
  totalPrice: 0,
  minPriceSurcharge: 0,
  tipAmount: 0,
};

export const computeAllowanceAmount = (allowanceAmount: number | null, monthlyEstimation: number) => {
  if (!allowanceAmount) return 0;

  // Allowance amount should not exceed 85% of the monthly estimation
  const maxAllowanceAmount = monthlyEstimation * 0.85;
  return Math.min(allowanceAmount, maxAllowanceAmount);
};

const calculateHourlyPriceForBasketPrestations = (
  chosenPrestations: PrestationReference[],
  universe: Universe,
  zone: ZoneId,
) => {
  // Reduce all prestations of the universe in an array
  const universePrestations = universe.categories.reduce(
    (acc1: Prestation[], category) =>
      acc1.concat(
        category.subcategories.reduce((acc2: Prestation[], subcategory) => acc2.concat(subcategory.prestations), []),
      ),
    [],
  );

  // Client can only choose one prestation at a time
  const childcarePrestation = chosenPrestations[0];
  const prestationInUniverse =
    !!childcarePrestation && universePrestations.find((p) => p.reference === childcarePrestation);
  const hourlyPrices = prestationInUniverse ? prestationInUniverse.prices : 0;

  return getPriceByZoneOrPriceItself(hourlyPrices, zone);
};

const getProPrice = (
  universe: Universe,
  categoryRef: CategoryReference,
  subCategoryRef: SubcategoryReference,
  prestationRef: PrestationReference,
  zone: ZoneId,
) => {
  const category = universe.categories.find((c) => c.reference === categoryRef);

  if (category) {
    const subCategory = category.subcategories.find((sc) => sc.reference === subCategoryRef);

    if (subCategory) {
      const prestation = subCategory.prestations.find((p) => p.reference === prestationRef);
      return prestation && prestation.pro_prices ? getPriceByZoneOrPriceItself(prestation.pro_prices, zone) : 0;
    }
  }

  return 0;
};

type ComputeChildcareSpotPricesArgs = {
  duration: number;
  giftCard?: GiftCard;
  hourlyPrice: number;
  primaryAppointment: Moment | null;
  promotion?: Promotion | null;
  tipAmount: number;
  universe: Universe;
};

const computeChildcareSpotPrices = ({
  duration,
  giftCard,
  hourlyPrice,
  primaryAppointment,
  promotion,
  tipAmount,
  universe,
}: ComputeChildcareSpotPricesArgs): ChildcareBasketPrices => {
  const { holidays } = universe;

  // Duration in hours
  const durationInHours = duration / 60;
  // Total price whithout adjustments
  const priceForFullDuration = hourlyPrice * durationInHours;

  // Appointment surcharge (Only a sunday or a holiday surcharge are considered)
  const hasSurcharge = isAppointmentDailySurcharged(primaryAppointment, holidays);
  const appointmentSurchargeReason = hasSurcharge
    ? getDailyAppointmentSurchargeReason(primaryAppointment, holidays)
    : undefined;
  const hourlyPriceSurcharge = hasSurcharge ? HOURLY_SURCHARGE : undefined;
  const appointmentSurcharge = hourlyPriceSurcharge ? hourlyPriceSurcharge * durationInHours : 0;

  const totalHourlyPrice = hourlyPriceSurcharge ? hourlyPrice + hourlyPriceSurcharge : hourlyPrice;
  const totalHourlyPriceAfterTax = totalHourlyPrice / 2;

  const totalPriceBeforeReductions = priceForFullDuration + appointmentSurcharge;

  // Promo and gift card values
  const { promoValue } = calculatePromoAndGiftValues({
    priceBeforeReduction: totalPriceBeforeReductions,
    promotion,
    giftCard,
    durationInHours,
  });
  // At the moment we do not allow for gift values for punctual childcare, only promo
  const giftValue = 0;

  // Total
  const totalPrice = Math.max(totalPriceBeforeReductions - (promoValue + giftValue), 0);

  return {
    kind: 'childcare',
    hourlyPrice: round(hourlyPrice),
    totalHourlyPrice: round(totalHourlyPrice),
    totalHourlyPriceAfterTax: round(totalHourlyPriceAfterTax),
    totalPrice: round(totalPrice),
    hourlyPriceSurcharge: hourlyPriceSurcharge ? round(hourlyPriceSurcharge) : undefined,
    appointmentSurcharge: round(appointmentSurcharge),
    appointmentSurchargeReason,
    promoValue: round(promoValue),
    giftValue: round(giftValue),
    // Currently not showing pro price for spot childcare
    proPrice: 0,
    // No min price for childcare, min duration is 2 hours and gets verified by screens
    minPriceSurcharge: 0,
    // Only needed for recurrent childcare
    monthlyEstimation: 0,
    allowanceAmount: 0,
    totalPriceAfterAllowance: 0,
    totalPriceTaxCredit: 0,
    weeklyTotalPrice: 0,
    tipAmount: round(tipAmount),
  };
};

type ComputeChildcareRecurrentPricesArgs = {
  universe: Universe;
  hourlyPrice: number;
  schedules?: Schedule[];
  childcareHoursPerWeek?: number;
  includeHolidays: boolean;
  allowanceAmount: number;
  tipAmount: number;
  zone: ZoneId;
  youngestChildAge?: YoungestChildAge;
};

const computeChildcareRecurrentPrices = ({
  universe,
  hourlyPrice,
  schedules,
  childcareHoursPerWeek,
  includeHolidays,
  allowanceAmount,
  tipAmount,
  zone,
  youngestChildAge,
}: ComputeChildcareRecurrentPricesArgs): ChildcareRecurrentBasketPrices => {
  // For recurrent childcare we use all hours of a month
  const weeksPerMonth = includeHolidays ? 4.5 : 3.5;
  const hoursPerWeek = childcareHoursPerWeek || getDurationPerWeekFromSchedules(schedules) / 60;
  const hoursPerMonth = hoursPerWeek * weeksPerMonth;

  const weeklyTotalPrice = hourlyPrice * hoursPerWeek;
  const totalPrice = hourlyPrice * hoursPerMonth;

  const proPrice = getProPrice(universe, CATEGORIES.BABYSITTING, RECURRING_CHILDCARE, RECURRING_CHILDCARE, zone);
  const totalProPrice = proPrice * hoursPerMonth;

  const isEligibleAllowance = isEligibleForAllowance({
    youngestChildAge,
  });
  const allowanceAmountComputed = isEligibleAllowance ? computeAllowanceAmount(allowanceAmount, totalProPrice) : 0;
  const totalPriceAfterAllowance = Math.max(totalPrice - allowanceAmountComputed, 0);
  const totalPriceTaxCredit = totalPriceAfterAllowance / 2;

  const monthlyEstimation = Math.max(totalPriceAfterAllowance - totalPriceTaxCredit, 0);
  const totalHourlyPriceAfterTax = monthlyEstimation && hoursPerMonth ? round(monthlyEstimation / hoursPerMonth) : 0;

  return {
    kind: 'childcare',
    hourlyPrice: round(hourlyPrice),
    totalHourlyPrice: round(hourlyPrice),
    totalPrice: round(totalPrice),
    weeklyTotalPrice: round(weeklyTotalPrice),
    allowanceAmount: round(allowanceAmountComputed),
    totalPriceAfterAllowance: round(totalPriceAfterAllowance),
    totalPriceTaxCredit: round(totalPriceTaxCredit),
    totalHourlyPriceAfterTax: round(totalHourlyPriceAfterTax),
    monthlyEstimation: round(monthlyEstimation),
    proPrice: round(proPrice),
    totalProPrice: round(totalProPrice),
    // No min price for childcare, min duration is 2 hours and gets verified by screens
    minPriceSurcharge: 0,
    // For now we are ignoring surcharges (But they might be added here later)
    hourlyPriceSurcharge: undefined,
    appointmentSurcharge: 0,
    appointmentSurchargeReason: undefined,
    // For now we are ignoring promo codes and gift cards (But they might be added here later)
    promoValue: 0,
    giftValue: 0,
    tipAmount: round(tipAmount),
  };
};

type ComputeChildcarePricesParams = {
  universe: Universe;
  primaryAppointment: Moment | null;
  basketContent: BasketContent;
  zone: ZoneId;
  schedules?: Schedule[];
  childcareHoursPerWeek?: number;
  includeHolidays?: boolean;
  allowanceAmount?: number;
  tipAmount?: number;
  duration?: number | null;
  promotion?: Promotion | null;
  giftCard?: GiftCard;
  youngestChildAge?: YoungestChildAge;
};

export const computeChildcarePrices = ({
  universe,
  primaryAppointment,
  basketContent,
  zone,
  duration,
  promotion,
  giftCard,
  youngestChildAge,
  schedules = [],
  childcareHoursPerWeek,
  includeHolidays = false,
  allowanceAmount = 0,
  tipAmount = 0,
}: ComputeChildcarePricesParams): ChildcareBasketPrices => {
  const basketPrestations = getAllBasketPrestations(basketContent);
  const isRecurrent = basketPrestations.includes(RECURRING_CHILDCARE);

  const hourlyPrice = calculateHourlyPriceForBasketPrestations(basketPrestations, universe, zone);

  return isRecurrent
    ? computeChildcareRecurrentPrices({
        universe,
        hourlyPrice,
        schedules,
        childcareHoursPerWeek,
        includeHolidays,
        allowanceAmount,
        tipAmount,
        zone,
        youngestChildAge,
      })
    : computeChildcareSpotPrices({
        universe,
        hourlyPrice,
        duration: duration || 0,
        primaryAppointment,
        promotion,
        giftCard,
        tipAmount,
      });
};
